// SourceForm.jsx - Formulaire de recherche de sources avec options avancées (adapté au nouveau style)
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Tooltip, CircularProgress, Chip, Box, FormControl, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { Search, Language, LocationOn, Settings, HelpOutline, FilterList, Business, School, Public, Article, Store, CheckCircle } from '@mui/icons-material';
import ProviderSelector from './SourceSelectProvider';

/**
 * Formulaire de recherche de sources avec options avancées - adapté au nouveau style
 * Version améliorée pour prendre en charge les requêtes suggérées
 */
const SourceForm = forwardRef(({ 
  onSearch, 
  initialValues = {}, 
  userAuthToken,
  competenceId = null,
  loading = false,
  onModeChange,
  hideObjectiveField = false,
  suggestedQuery = null, // Nouvelle prop pour recevoir la requête suggérée
  semanticAnalysis = null // Nouvelle prop pour recevoir l'analyse sémantique complète
}, ref) => {
  // États du formulaire
  const [query, setQuery] = useState(initialValues.query || '');
  const [objective, setObjective] = useState(initialValues.objective || '');
  const [advancedOptions, setAdvancedOptions] = useState(false);
  
  // État pour suivre si une requête suggérée est utilisée
  const [usingSuggestedQuery, setUsingSuggestedQuery] = useState(false);
  
  // Mise à jour pour gérer plusieurs langues
  const [userLanguages, setUserLanguages] = useState(initialValues.languages || ['en']);
  
  // Mise à jour pour rendre le pays optionnel
  const [userCountry, setUserCountry] = useState(initialValues.country || '');
  const [useCountry, setUseCountry] = useState(initialValues.country ? true : false);
  
  const [maxSources, setMaxSources] = useState(initialValues.maxSources || 20);
  const [selectedProviders, setSelectedProviders] = useState(initialValues.selectedProviders || []);
  const [selectedMode, setSelectedMode] = useState(initialValues.providerMode || 'auto');
  const [preferredTypes, setPreferredTypes] = useState(initialValues.preferredTypes || []);
  const [useAI, setUseAI] = useState(initialValues.useAI !== false);
  const [error, setError] = useState(null);
  
  // Référence au composant ProviderSelector
  const providerSelectorRef = React.useRef(null);
  
  // Exposer des méthodes au parent via la référence
  useImperativeHandle(ref, () => ({
    updateProviderRecommendations: (recommendations) => {
      if (providerSelectorRef.current && typeof providerSelectorRef.current.updateRecommendations === 'function') {
        providerSelectorRef.current.updateRecommendations(recommendations);
      }
    }
  }));
  
  // Mettre à jour les valeurs initiales lorsqu'elles changent
  useEffect(() => {
    if (initialValues.query) setQuery(initialValues.query);
    if (initialValues.objective) setObjective(initialValues.objective);
    if (initialValues.languages) setUserLanguages(initialValues.languages);
    if (initialValues.country) {
      setUserCountry(initialValues.country);
      setUseCountry(true);
    }
    if (initialValues.maxSources) setMaxSources(initialValues.maxSources);
    if (initialValues.selectedProviders) setSelectedProviders(initialValues.selectedProviders);
    if (initialValues.providerMode) setSelectedMode(initialValues.providerMode);
    if (initialValues.preferredTypes) setPreferredTypes(initialValues.preferredTypes);
    if (initialValues.useAI !== undefined) setUseAI(initialValues.useAI);
  }, [initialValues]);
  
  // Effet pour appliquer la requête suggérée lorsqu'elle est fournie
  useEffect(() => {
    if (suggestedQuery && suggestedQuery.trim()) {
      console.log("Requête suggérée reçue:", suggestedQuery);
      setQuery(suggestedQuery);
      setUsingSuggestedQuery(true);
      
      // Si l'analyse sémantique est disponible, utiliser également les types de contenu préférés
      if (semanticAnalysis && semanticAnalysis.contentTypes && semanticAnalysis.contentTypes.length > 0) {
        console.log("Types de contenu de l'analyse sémantique:", semanticAnalysis.contentTypes);
        
        // Mapper les types de contenu de l'analyse vers les types de sources
        const mappedTypes = [];
        
        if (semanticAnalysis.contentTypes.includes('academic')) {
          mappedTypes.push('Educational');
        }
        if (semanticAnalysis.contentTypes.includes('factual') || semanticAnalysis.contentTypes.includes('reference')) {
          mappedTypes.push('Governmental');
        }
        if (semanticAnalysis.contentTypes.includes('commercial')) {
          mappedTypes.push('Commercial');
        }
        if (semanticAnalysis.contentTypes.includes('opinion') || semanticAnalysis.contentTypes.includes('news')) {
          mappedTypes.push('Media');
        }
        
        if (mappedTypes.length > 0) {
          console.log("Types de sources mappés depuis l'analyse:", mappedTypes);
          setPreferredTypes(mappedTypes);
        }
      }
    }
  }, [suggestedQuery, semanticAnalysis]);
  
  // Effet pour mettre en évidence le champ de requête lorsqu'une suggestion est utilisée
  useEffect(() => {
    if (usingSuggestedQuery) {
      // Sélectionner le champ de requête après un court délai
      const timer = setTimeout(() => {
        const queryInput = document.getElementById('search-query-input');
        if (queryInput) {
          queryInput.focus();
          queryInput.classList.add('border-green-500', 'bg-green-50');
          
          // Retirer la mise en évidence après 2 secondes
          setTimeout(() => {
            queryInput.classList.remove('border-green-500', 'bg-green-50');
          }, 2000);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [usingSuggestedQuery]);
  
  // Langues disponibles
  const availableLanguages = [
    { code: 'en', name: 'Anglais' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Espagnol' },
    { code: 'de', name: 'Allemand' },
    { code: 'it', name: 'Italien' },
    { code: 'pt', name: 'Portugais' },
    { code: 'nl', name: 'Néerlandais' },
    { code: 'ru', name: 'Russe' },
    { code: 'zh', name: 'Chinois' },
    { code: 'ja', name: 'Japonais' },
    { code: 'ar', name: 'Arabe' }
  ];
  
  // Pays disponibles
  const availableCountries = [
    { code: '', name: 'Non spécifié (mondial)' },
    { code: 'FR', name: 'France' },
    { code: 'US', name: 'États-Unis' },
    { code: 'CA', name: 'Canada' },
    { code: 'BE', name: 'Belgique' },
    { code: 'CH', name: 'Suisse' },
    { code: 'UK', name: 'Royaume-Uni' },
    { code: 'ES', name: 'Espagne' },
    { code: 'DE', name: 'Allemagne' },
    { code: 'IT', name: 'Italie' },
    { code: 'NL', name: 'Pays-Bas' },
    { code: 'MA', name: 'Maroc' },
    { code: 'BR', name: 'Brésil' },
    { code: 'MX', name: 'Mexique' },
    { code: 'AU', name: 'Australie' },
    { code: 'JP', name: 'Japon' },
    { code: 'CN', name: 'Chine' },
    { code: 'RU', name: 'Russie' },
    { code: 'IN', name: 'Inde' },
    { code: 'ZA', name: 'Afrique du Sud' }
  ];
  
  // Types de sources disponibles
  const sourceTypes = [
    { value: 'Governmental', label: 'Gouvernemental', icon: <Public className="h-5 w-5 text-blue-600" /> },
    { value: 'Educational', label: 'Éducatif', icon: <School className="h-5 w-5 text-green-600" /> },
    { value: 'Commercial', label: 'Commercial', icon: <Store className="h-5 w-5 text-purple-600" /> },
    { value: 'Non-profit', label: 'Organisation à but non lucratif', icon: <Business className="h-5 w-5 text-yellow-600" /> },
    { value: 'Media', label: 'Média', icon: <Article className="h-5 w-5 text-red-600" /> }
  ];
  
  /**
   * Gère la sélection des langues
   */
  const handleLanguagesChange = (event) => {
    const {
      target: { value },
    } = event;
    setUserLanguages(
      // On "value" type is always an array, even when only one language is selected
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  /**
   * Gère le changement de mode et propage l'événement au parent
   * @param {string} mode - Nouveau mode sélectionné
   */
  const handleModeChange = (mode) => {
    console.log("SourceForm: Mode changé en", mode);
    
    // Mettre à jour l'état local
    setSelectedMode(mode);
    
    // Notifier le parent du changement de mode
    if (onModeChange && typeof onModeChange === 'function') {
      console.log("SourceForm: Notification du changement de mode au parent");
      onModeChange(mode);
    }
  };

  /**
   * Fonction centralisée pour mettre à jour les paramètres SANS déclencher de recherche
   * @param {Object|Array|string} updates - Mises à jour à appliquer
   */
  const updateParams = (updates) => {
    console.log("Mise à jour des paramètres sans recherche:", updates);
    
    // Si updates est un tableau, c'est une mise à jour de providers
    if (Array.isArray(updates)) {
      setSelectedProviders(updates);
      return;
    }
    
    // Si updates est un simple string, c'est un changement de mode
    if (typeof updates === 'string') {
      setSelectedMode(updates);
      return;
    }
    
    // Si c'est un objet avec _internal, c'est une mise à jour interne depuis SourceSelectProvider
    if (typeof updates === 'object' && updates._internal) {
      // Mise à jour silencieuse qui ne doit pas déclencher de recherche
      if (updates.selectedProviders !== undefined) {
        setSelectedProviders(updates.selectedProviders);
      }
      return;
    }
    
    // Sinon c'est un objet standard avec différentes mises à jour
    if (typeof updates === 'object') {
      if (updates.selectedProviders !== undefined) setSelectedProviders(updates.selectedProviders);
      if (updates.selectedMode !== undefined) setSelectedMode(updates.selectedMode);
      if (updates.query !== undefined) setQuery(updates.query);
      if (updates.objective !== undefined) setObjective(updates.objective);
      if (updates.userLanguages !== undefined) setUserLanguages(updates.userLanguages);
      if (updates.userCountry !== undefined) setUserCountry(updates.userCountry);
      if (updates.useCountry !== undefined) setUseCountry(updates.useCountry);
      if (updates.maxSources !== undefined) setMaxSources(updates.maxSources);
      if (updates.preferredTypes !== undefined) setPreferredTypes(updates.preferredTypes);
      if (updates.useAI !== undefined) setUseAI(updates.useAI);
    }
  };
  
  /**
   * Fonction dédiée pour déclencher la recherche UNIQUEMENT via le bouton
   */
  const triggerSearch = () => {
    console.log("BOUTON RECHERCHE CLIQUÉ");
    
    setError(null);
    
    // Validation: seul l'objectif est obligatoire
    if (!objective.trim()) {
      setError('Veuillez définir un objectif.');
      return;
    }

        // JUSTE AVANT: const searchParams = {
    console.log("🚨 DEBUG TRANSMISSION PAYS:", {
      "checkbox_useCountry": useCountry,
      "dropdown_userCountry": userCountry,
      "type_userCountry": typeof userCountry,
      "checkbox_DOM": document.getElementById('use-country')?.checked,
      "select_DOM": document.querySelector('select[value]')?.value,
      "condition_test": useCountry ? userCountry : 'VIDE'
    });
   
    // Collecter tous les paramètres de recherche
    const searchParams = {
      query: query.trim(), // Peut être vide
      objective: objective.trim(),
      languages: userLanguages,
     country: useCountry && userCountry ? userCountry : '',
      maxSources,
      selectedProviders: selectedProviders, // Toujours envoyer les providers sélectionnés, quel que soit le mode
      providerMode: selectedMode,
      preferredTypes: preferredTypes.length > 0 ? preferredTypes : null,
      useAI,
      competenceId,
      _fromButton: true, // Marqueur spécial pour indiquer une recherche explicite
      fromSuggestedQuery: usingSuggestedQuery, // Nouvel indicateur pour savoir si on utilise une requête suggérée
      semanticAnalysis: semanticAnalysis // Envoyer l'analyse sémantique pour traitement avancé côté serveur
    };
    
    console.log("RECHERCHE DÉCLENCHÉE - Providers sélectionnés:", selectedProviders);
    console.log("Utilisation d'une requête suggérée:", usingSuggestedQuery);
    
    // Transmettre les paramètres au parent
    onSearch && onSearch(searchParams);
  };
  
  /**
   * Gère la sélection/désélection d'un type de source
   */
  const handleTypeToggle = (type) => {
    setPreferredTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };
  
  /**
   * Réinitialise la requête suggérée pour revenir à une recherche manuelle
   */
  const handleResetSuggestedQuery = () => {
    setQuery('');
    setUsingSuggestedQuery(false);
  };
  
  // Rendu du composant
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      {/* Nous utilisons une div au lieu d'un form pour éviter toute soumission automatique */}
      <div className="space-y-6 max-w-3xl mx-auto">
      
        {/* Champs principaux */}
        <div className="space-y-4">
          {/* Champ d'objectif - obligatoire - affiché seulement si hideObjectiveField est false */}
          {!hideObjectiveField && (
            <div>
              <label className="block text-gray-700 mb-2 font-medium">
                Objectif de recherche <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  className="w-full p-3 pl-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder="Décrivez ce que vous recherchez..."
                  rows={2}
                  required
                  disabled={loading}
                />
                <Tooltip title="Décrivez clairement votre objectif de recherche. Par exemple : 'Je cherche des informations sur les méthodes d'isolation thermique écologiques pour une maison ancienne'" placement="top">
                  <HelpOutline className="absolute right-3 top-3 text-gray-400" />
                </Tooltip>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-1">
                Précisez votre objectif de recherche de manière claire et concise
              </p>
            </div>
          )}
          

        </div>
        
        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        
        {/* Options avancées */}
        <div>
          <button
            type="button"
            onClick={() => setAdvancedOptions(!advancedOptions)}
            className="flex items-center text-purple-600 hover:text-purple-800 font-medium"
          >
            <Settings className="h-5 w-5 mr-2" />
            {advancedOptions ? "Masquer les options avancées" : "Afficher les options avancées"}
            <span className="ml-2">
              {advancedOptions ? 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg> : 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              }
            </span>
          </button>
        </div>
        
        {advancedOptions && (
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">

          {/* Champ de requête - facultatif - avec indication spéciale si requête suggérée */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Requête spécifique
              <span className="text-sm font-normal ml-2">
                {usingSuggestedQuery ? (
                  <span className="text-green-600">
                    (requête suggérée)
                  </span>
                ) : (
                  <span className="text-gray-500">
                    (facultatif)
                  </span>
                )}
              </span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${usingSuggestedQuery ? 'text-green-500' : 'text-gray-400'}`} />
              </div>
              <input
                id="search-query-input"
                type="text"
                className={`w-full p-3 pl-10 pr-10 border ${
                  usingSuggestedQuery
                    ? 'border-green-300 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                    : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                } rounded-lg`}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  // Si l'utilisateur modifie la requête, on ne considère plus qu'elle vient d'une suggestion
                  if (usingSuggestedQuery) {
                    setUsingSuggestedQuery(false);
                  }
                }}
                placeholder={
                  usingSuggestedQuery
                    ? "Requête suggérée appliquée automatiquement"
                    : "Préciser vos instructions de recherche (facultatif)"
                }
                disabled={loading}
              />
              <Tooltip title={
                usingSuggestedQuery 
                  ? "Cette requête a été générée et optimisée pour votre objectif"
                  : "Soyez précis dans votre requête. Vous pouvez mentionner des technologies, concepts ou méthodes particulières."
              } placement="top">
                <HelpOutline className="absolute right-3 top-3 text-gray-400" />
              </Tooltip>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-1">
              {usingSuggestedQuery
                ? "Cette requête a été générée et optimisée pour votre objectif"
                : "Facultatif: précisez des critères particuliers, technologies, lieux, ou exigences spécifiques..."}
            </p>
          </div>
          
            {/* Section des langues et pays */}
            <div className="mb-6">
              <h3 className="text-gray-700 font-medium mb-3">Paramètres régionaux et linguistiques</h3>
              
              {/* Sélection multiple de langues */}
              <div className="mb-4">
                <label className="block text-gray-700 mb-2 font-medium flex items-center">
                  <Language className="h-5 w-5 mr-2 text-blue-500" />
                  Langues des résultats
                  <Tooltip title="Sélectionnez une ou plusieurs langues pour vos résultats. Par défaut, l'anglais est sélectionné pour maximiser les résultats." placement="top">
                    <HelpOutline className="ml-2 h-4 w-4 text-gray-400" />
                  </Tooltip>
                </label>
                <FormControl fullWidth>
                  <Select
                    multiple
                    value={userLanguages}
                    onChange={handleLanguagesChange}
                    input={<OutlinedInput className="bg-white" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const lang = availableLanguages.find(l => l.code === value);
                          return <Chip key={value} label={lang ? lang.name : value} size="small" />;
                        })}
                      </Box>
                    )}
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg"
                  >
                    {availableLanguages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        <Checkbox checked={userLanguages.indexOf(lang.code) > -1} />
                        <ListItemText primary={lang.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Astuce: Sélectionnez plusieurs langues pour élargir vos résultats. L'anglais est recommandé pour une couverture maximale.
                </p>
              </div>
              
              {/* Option de pays avec possibilité de désactiver */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="use-country"
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    checked={useCountry}
                    onChange={(e) => setUseCountry(e.target.checked)}
                    disabled={loading}
                  />
                  <label htmlFor="use-country" className="ml-2 block text-gray-700 font-medium flex items-center">
                    <LocationOn className="h-5 w-5 mr-2 text-red-500" />
                    Cibler une région géographique
                    <Tooltip title="Si activé, limite les résultats à un pays spécifique. Désactivez cette option pour une recherche mondiale sans restriction géographique." placement="top">
                      <HelpOutline className="ml-2 h-4 w-4 text-gray-400" />
                    </Tooltip>
                  </label>
                </div>
                
                {useCountry && (
                  <select
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    value={userCountry}
                    onChange={(e) => setUserCountry(e.target.value)}
                    disabled={loading || !useCountry}
                  >
                    {availableCountries.filter(c => c.code !== '').map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                )}
                
                <p className="text-xs text-gray-500 mt-1">
                  {!useCountry 
                    ? "Aucune restriction géographique: recherche mondiale pour des résultats plus diversifiés."
                    : "Restriction géographique activée: résultats optimisés pour la région sélectionnée."}
                </p>
              </div>
            </div>
                        
            {/* Nombre maximum de sources */}
            <div className="mb-6">
              <label className="block text-gray-700 mb-2 font-medium">Nombre maximum de sources</label>
              <select
                className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={maxSources}
                onChange={(e) => setMaxSources(Number(e.target.value))}
                disabled={loading}
              >
                <option value={5}>5 sources</option>
                <option value={10}>10 sources</option>
                <option value={15}>15 sources</option>
                <option value={20}>20 sources</option>
                <option value={30}>30 sources</option>
              </select>
            </div>
            
            {/* Option d'utilisation de l'IA */}
            <div className="flex items-start">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                checked={useAI}
                onChange={(e) => setUseAI(e.target.checked)}
                disabled={loading}
                id="use-ai"
              />
              <label htmlFor="use-ai" className="ml-2 block text-gray-700">
                <span className="font-medium">Utiliser l'IA pour améliorer les résultats</span>
                <Tooltip title="L'IA analyse votre requête et votre objectif pour recommander les meilleures sources et fournisseurs. Désactiver cette option accélère la recherche mais peut réduire la pertinence." placement="top">
                  <HelpOutline className="ml-1 h-4 w-4 text-gray-400 inline-block" />
                </Tooltip>
                <p className="text-xs text-gray-500 mt-1">L'IA analysera votre objectif et requête pour trouver les sources les plus pertinentes</p>
              </label>
            </div>
          </div>
        )}
        
{/* Sélecteur de fournisseurs */}
<div className="mt-6">
          <h4 className="font-medium text-gray-800 mb-3">Sources de données:</h4>

          <ProviderSelector
            ref={providerSelectorRef}
            selectedProviders={selectedProviders}
            onChange={updateParams}
            userAuthToken={userAuthToken}
            initialMode={selectedMode}
            onModeChange={handleModeChange}
            semanticAnalysis={semanticAnalysis} // Passer les données d'analyse pour optimiser les recommandations
          />          
        </div>

        {/* Bouton de recherche */}
        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={triggerSearch}
            className={`flex items-center justify-center ${
              usingSuggestedQuery 
                ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700'
                : 'bg-purple-600 hover:bg-purple-700'
            } text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-md`}
            disabled={loading || !objective.trim()}
          >
            {loading ? (
              <CircularProgress size={24} className="mr-2 text-white" />
            ) : (
              <Search className="h-5 w-5 mr-2" />
            )}
            {usingSuggestedQuery 
              ? 'Rechercher avec la requête suggérée'
              : 'Rechercher des sources'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default SourceForm;